/** How close to an edge (px) the pointer has to get before the container starts scrolling. */
const EDGE_PX = 56;
/** Pixels per frame at the very edge, tapering to nothing at the start of the zone. */
const MAX_STEP_PX = 18;

/**
 * The nearest ancestor that actually scrolls, or null.
 *
 * Found by asking each ancestor rather than by naming the container: the anchor's node view is
 * mounted inside whichever surface is hosting the editor, and a test id or class would tie a
 * gesture to one of them.
 */
export const scrollableAncestor = (from: HTMLElement): HTMLElement | null => {
	let element: HTMLElement | null = from.parentElement;
	while (element !== null) {
		const { overflowY } = window.getComputedStyle(element);
		if (
			(overflowY === 'auto' || overflowY === 'scroll') &&
			element.scrollHeight > element.clientHeight
		) {
			return element;
		}
		element = element.parentElement;
	}
	return null;
};

/**
 * Scrolls the container when a drag nears its top or bottom edge.
 *
 * HTML5 drag-and-drop gets this from the browser; a pointer-driven drag does not, and without it
 * an anchor can only be moved as far as the part of the document already on screen — which on a
 * deed is usually not as far as the execution block.
 */
export const autoScroll = (container: HTMLElement | null, clientY: number): void => {
	if (container === null) {
		return;
	}
	const { top, bottom } = container.getBoundingClientRect();
	const overTop = clientY - top;
	const overBottom = bottom - clientY;
	if (overTop < EDGE_PX) {
		// Fastest at the edge, nothing at the start of the zone, so the speed is something the
		// hand can aim rather than a constant lurch.
		container.scrollTop -= MAX_STEP_PX * (1 - Math.max(0, overTop) / EDGE_PX);
		return;
	}
	if (overBottom < EDGE_PX) {
		container.scrollTop += MAX_STEP_PX * (1 - Math.max(0, overBottom) / EDGE_PX);
	}
};
